// Convite para o changelog publicado pela landing. Cada versão abre uma vez:
// qualquer forma de fechar registra a versão atual como vista.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  CHANGELOG_LAST_SEEN_KEY,
  CHANGELOG_PAGE_URL,
  fetchChangelog,
  type ChangelogRelease,
} from "./changelog-service.js";
import "./i18n.js";

export function WhatsNewDialog() {
  const { t } = useTranslation();
  const [release, setRelease] = useState<ChangelogRelease | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    void fetchChangelog(controller.signal)
      .then((feed) => {
        if (localStorage.getItem(CHANGELOG_LAST_SEEN_KEY) === feed.latestVersion) return;
        setRelease(feed.releases[0] ?? null);
        setOpen(true);
      })
      .catch(() => {
        // A landing ou o storage indisponíveis nunca devem bloquear o produto.
      });
    return () => controller.abort();
  }, []);

  function markAsSeen() {
    if (release) {
      try {
        localStorage.setItem(CHANGELOG_LAST_SEEN_KEY, release.version);
      } catch {
        // Conteúdo opcional continua acessível mesmo sem storage.
      }
    }
    setOpen(false);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      markAsSeen();
      return;
    }
    setOpen(true);
  }

  if (!release) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent showCloseButton={false} className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{release.title}</DialogTitle>
          <DialogDescription>{release.summary}</DialogDescription>
        </DialogHeader>

        <ul className="list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-muted-foreground">
          {release.changes.map((change) => (
            <li key={change}>{change}</li>
          ))}
        </ul>

        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>{t("whatsNew.notNow")}</DialogClose>
          <Button
            render={
              <a href={CHANGELOG_PAGE_URL} target="_blank" rel="noreferrer" onClick={markAsSeen} />
            }
            nativeButton={false}
            role="link"
          >
            {t("whatsNew.view")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
